import { posthogCaptureWithUserContext } from "./general";
import {
  getLiveGamePropertiesForEvent,
  getWorksheetPropertiesForEvent,
} from "./properties";

/**
 * Captures the event the user creates a live game.
 *
 * @param {Object} properties
 * @param {string} properties.triggerId The ID of the trigger.
 * @param {number} properties.gid The ID of the live game created.
 * @param {Parameters<typeof getLiveGamePropertiesForEvent>[1]} properties.options The options for the live game.
 * @param {Parameters<typeof getWorksheetPropertiesForEvent>[0] | undefined} properties.worksheet The worksheet for the live game.
 */
export function posthogEventLiveGameCreated({
  triggerId,
  gid,
  options,
  worksheet,
}) {
  const liveGameProperties = gid && options ? getLiveGamePropertiesForEvent(gid, options) : {};
  const worksheetProperties = worksheet ? getWorksheetPropertiesForEvent(worksheet) : {};
  posthogCaptureWithUserContext("live_game_created", {
    trigger_id: triggerId,
    ...liveGameProperties,
    ...worksheetProperties,
  });
}

/**
 * Captures the event the user starts a live game.
 *
 * @param {Object} properties
 * @param {string} properties.triggerId The ID of the trigger.
 * @param {number} properties.gid The ID of the live game started.
 * @param {number} properties.numberOfParticipants The number of participants in the live game.
 */
export function posthogEventLiveGameStarted({
  triggerId,
  gid,
  numberOfParticipants,
}) {
  posthogCaptureWithUserContext("live_game_started", {
    trigger_id: triggerId,
    live_game_id: gid,
    number_of_participants: numberOfParticipants,
  });
}

/**
 * Captures the event a live game question is complete.
 *
 * @param {Object} properties
 * @param {string} properties.triggerId The ID of the trigger.
 * @param {number} properties.gid The ID of the live game.
 * @param {number} properties.questionNumber The question number.
 * @param {Object} properties.question The question object.
 * @param {number | undefined} properties.question.qid The exam question ID.
 * @param {{ type: string }} properties.question.answer The question answer object.
 * @param {{ ssid: number } | undefined} properties.question.subskill The question subskill object.
 * @param {number} properties.numberOfParticipants The number of participants in the live game.
 * @param {number} properties.numberOfParticipantsAnswered The number of participants who have answered the question.
 */
export function posthogEventLiveGameQuestionComplete({
  triggerId,
  gid,
  questionNumber,
  question,
  numberOfParticipants,
  numberOfParticipantsAnswered,
}) {
  const { qid, subskill, answer } = question ?? {};
  const { type: questionInputType } = answer ?? {};
  const { ssid } = subskill ?? {};
  posthogCaptureWithUserContext("live_game_question_complete", {
    trigger_id: triggerId,
    live_game_id: gid,
    question_number: questionNumber,
    question_type: qid ? "exam_question" : "question_generator",
    question_qid: qid,
    question_ssid: qid ? undefined : ssid,
    question_input_type: questionInputType,
    number_of_participants: numberOfParticipants,
    number_of_participants_answered: numberOfParticipantsAnswered,
  });
}

/**
 * Captures the event the user finishes a live game.
 *
 * @param {Object} properties
 * @param {string} properties.triggerId The ID of the trigger.
 * @param {number} properties.gid The ID of the live game.
 * @param {number} properties.numberOfParticipants The number of participants that finished the live game.
 * @param {number} properties.questionsCompleted The number of questions completed in the live game.
 */
export function posthogEventLiveGameFinished({
  triggerId,
  gid,
  numberOfParticipants,
  questionsCompleted,
}) {
  posthogCaptureWithUserContext("live_game_finished", {
    trigger_id: triggerId,
    live_game_id: gid,
    number_of_participants: numberOfParticipants,
    questions_completed: questionsCompleted,
  });
}
